<template>
  <div ref="container" class="ortho-container" :class="{ maximized }" :style="maximized ? {} : { width: width, height: height }">
    <canvas ref="canvas"></canvas>
    <div class="ortho-label">{{ side === 'left' ? $t('viewer.leftView') : $t('viewer.rightView') }}</div>
    <div class="ortho-zoom">{{ zoom.toFixed(1) }}x</div>
    <button class="ortho-expand" @click.stop="toggleMaximize">{{ maximized ? '×' : '⛶' }}</button>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import * as THREE from 'three'
import type { ExternalRenderer } from '../core/SceneManager'

export default Vue.extend({
  name: 'OrthoViewer',

  props: {
    scene: { type: Object as Vue.PropType<THREE.Scene | null>, default: null },
    side: { type: String as Vue.PropType<'left' | 'right'>, default: 'left' },
    backgroundColor: { type: Number, default: 0x12122a },
    width: { type: [Number, String], default: '100%' },
    height: { type: [Number, String], default: '100%' },
    target: { type: Object as Vue.PropType<THREE.Vector3 | null>, default: null },
  },

  data() {
    return {
      renderer: null as THREE.WebGLRenderer | null,
      orthoCam: null as THREE.OrthographicCamera | null,
      zoom: 1,
      baseZoom: 3,
      orthoDist: 10,
      maximized: false,
    }
  },

  computed: {
    externalRenderer(): ExternalRenderer | null {
      const r = (this as any).renderer
      const c = (this as any).orthoCam
      const s = (this as any).scene
      if (!r || !c || !s) return null
      return {
        render: () => {
          ;(this as any).updateCameraTarget()
          if (r && c && s) r.render(s, c)
        },
        dispose: () => {
          r.dispose()
        },
      }
    },
  },

  mounted() {
    const canvas = this.$refs.canvas as HTMLCanvasElement
    if (!canvas) return

    const cw = canvas.clientWidth || 200
    const ch = canvas.clientHeight || 150
    const aspect = cw / ch

    const cam = new THREE.OrthographicCamera(-this.baseZoom * aspect, this.baseZoom * aspect, this.baseZoom, -this.baseZoom, 0.1, 1000)
    this.orthoCam = cam
    this.fitToScene()

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    renderer.setSize(cw, ch)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(this.backgroundColor)
    this.renderer = renderer

    const wheel = (e: WheelEvent) => {
      e.preventDefault()
      this.zoom = Math.max(0.1, Math.min(10, this.zoom * (e.deltaY > 0 ? 1.1 : 0.9)))
      this.fitCamera()
    }
    canvas.addEventListener('wheel', wheel, { passive: false })

    const resize = () => {
      if (!renderer || !cam) return
      const container = this.$refs.container as HTMLElement | null
      const w = container?.clientWidth || 200
      const h = container?.clientHeight || 150
      if (w > 0 && h > 0) {
        renderer.setSize(w, h)
        this.fitCamera()
      }
    }
    window.addEventListener('resize', resize)

    this.$once('hook:beforeDestroy', () => {
      canvas.removeEventListener('wheel', wheel)
      window.removeEventListener('resize', resize)
      if (this.renderer) this.renderer.dispose()
    })
  },

  watch: {
    maximized: {
      handler(this: Vue) {
        this.$nextTick(() => {
          ;(this as any).handleResize()
        })
      },
    },
  },

  methods: {
    getExternalRenderer(): ExternalRenderer | null {
      const r = this.renderer
      const c = this.orthoCam
      const s = this.scene
      if (!r || !c || !s) return null
      return {
        render: () => {
          this.updateCameraTarget()
          r.render(s, c)
        },
        dispose: () => r.dispose(),
      }
    },

    fitToScene() {
      const s = this.scene
      const cam = this.orthoCam
      if (!s || !cam) return
      const box = new THREE.Box3()
      s.traverse(obj => {
        if (obj instanceof THREE.Mesh && obj.geometry) {
          box.expandByObject(obj)
        }
      })
      if (box.isEmpty()) return
      const size = box.getSize(new THREE.Vector3())
      const maxDim = Math.max(size.x, size.y, size.z)
      const center = box.getCenter(new THREE.Vector3())
      const dist = Math.max(maxDim * 1.5, 0.5)
      const sideSign = this.side === 'left' ? -1 : 1
      cam.position.set(center.x + sideSign * dist, center.y, center.z)
      cam.lookAt(center)
      this.orthoDist = dist
      if (maxDim > 0) {
        this.baseZoom = maxDim * 0.6
        this.fitCamera()
      }
    },

    fitCamera() {
      const c = this.orthoCam
      if (!c) return
      const canvas = this.$refs.canvas as HTMLCanvasElement | null
      const w = canvas?.clientWidth || 200
      const h = canvas?.clientHeight || 150
      const aspect = w / h
      const s = (this.baseZoom || 3) / (this.zoom || 1)
      c.left = -s * aspect
      c.right = s * aspect
      c.top = s
      c.bottom = -s
      c.updateProjectionMatrix()
    },

    updateCameraTarget() {
      const t = this.target
      const cam = this.orthoCam
      if (!t || !cam) return
      const sideSign = this.side === 'left' ? -1 : 1
      cam.position.set(t.x + sideSign * this.orthoDist, t.y, t.z)
      cam.lookAt(t)
    },

    toggleMaximize() {
      this.maximized = !this.maximized
    },

    handleResize() {
      const r = this.renderer
      const cam = this.orthoCam
      if (!r || !cam) return
      let w: number, h: number
      if (this.maximized) {
        w = window.innerWidth
        h = window.innerHeight
      } else {
        const container = this.$refs.container as HTMLElement | null
        w = container?.clientWidth || 200
        h = container?.clientHeight || 150
      }
      if (w > 0 && h > 0) {
        r.setSize(w, h)
        this.fitCamera()
      }
    },
  },
})
</script>

<style scoped>
.ortho-container {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #2a2a3e;
}
.ortho-container canvas {
  display: block;
  width: 100%;
  height: 100%;
}
.ortho-label {
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #888;
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
}
.ortho-zoom {
  position: absolute;
  bottom: 6px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: #666;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  pointer-events: none;
}
.ortho-expand {
  position: absolute;
  top: 4px;
  right: 6px;
  background: rgba(0, 0, 0, 0.5);
  color: #888;
  border: none;
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  z-index: 5;
}
.ortho-expand:hover { background: rgba(0, 0, 0, 0.8); color: #fff; }
.ortho-container.maximized {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  border-radius: 0;
  border: none;
}
.ortho-container.maximized canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>
